<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useInfiniteScroll, useTitle } from '@vueuse/core'
import { useRoute, useRouter } from 'vue-router/composables'
import { commonTexts } from './configs/common'
import CollectionPageDefault from './components/CollectionPage.default.vue'
import CollectionPageFlipnovel from './components/CollectionPage.flipnovel.vue'
import { api } from '@/api'
import { brand } from '@/config/brands'
import { useFinishedClick, useFinishedView } from '@/composables/tracks'
import { loadBrandComponent } from '@/utils/loadBrandConfig'
import { READER_ROUTE_NAME } from '@/utils/readerRouteName'

const props = withDefaults(
  defineProps<{
    columnId: string
    type?: 'recommend' | 'ordinary' | 'finish'
  }>(),
  {
    type: 'ordinary',
  },
)

const CollectionPage = loadBrandComponent({
  rocnovel: CollectionPageDefault,
  oceread: CollectionPageDefault,
  talewave: CollectionPageDefault,
  ficmoon: CollectionPageDefault,
  novelarc: CollectionPageDefault,
  storyella: CollectionPageDefault,
  aurenovel: CollectionPageDefault,
  silktale: CollectionPageDefault,
  pearla: CollectionPageDefault,
  fictora: CollectionPageDefault,
  marinovel: CollectionPageDefault,
  flipnovel: CollectionPageFlipnovel,
  moonpage: CollectionPageFlipnovel,
  novelune: CollectionPageFlipnovel,
  storlyn: CollectionPageFlipnovel,
  novaris: CollectionPageFlipnovel,
  novelmello: CollectionPageDefault,
})

const route = useRoute()
const router = useRouter()
const isFlipnovelFamily = ['flipnovel', 'storlyn', 'moonpage', 'novaris', 'novelune'].includes(brand)
const { finishedView } = useFinishedView()
const { finishedClick } = useFinishedClick()
const allData = ref<any>({})
const title = useTitle('column')
const collections = ref<any>([])
const page = ref(1)
const fetching = ref(false)
const ending = ref(false)
const error = ref<any>()
const pagination = computed(() => ({
  pageIndex: page.value,
  pageSize: 20,
}))

function resetState() {
  collections.value = []
  page.value = 1
  ending.value = false
  fetching.value = false
  error.value = undefined
}

const getCollectionByType = async function (type = 'ordinary') {
  let result: any = []
  if (type === 'recommend')
    result = await api.get<any, GetContentGetcolumncontentlistResponse>('/content/getLikeColumnContentList', { params: { columnId: props.columnId, ...pagination.value } })
  else if (type === 'finish')
    result = await api.get<any, GetContentGetfinishlikecolumnResponse>('/content/getFinishLikeColumn', { params: { contentId: props.columnId, pageIndex: 1, pageSize: 30 } })
  else
    result = await api.get<any, GetContentGetcolumncontentlistResponse>('/content/getColumnContentList', { params: { columnId: props.columnId, ...pagination.value } })

  return result.data
}

async function fetchCollection() {
  if (fetching.value || ending.value)
    return
  fetching.value = true
  try {
    const data = await getCollectionByType(route.query.type as string)

    // 对于 finish 类型，最多只展示30条数据
    if (route.query.type === 'finish') {
      collections.value = data.resourceList.records || []
      ending.value = true

      // 完读推荐列表页曝光埋点
      finishedView({
        contentId: props.columnId,
        serialStatus: route.query.status === 'finish' ? 'completed' : 'ongoing',
        showNum: collections.value.length,
        title: 'finished_reclist',
      })
    }
    else {
      // 其他类型保持原有的分页逻辑
      collections.value = [...collections.value, ...data.resourceList.records]
      if (page.value >= data.resourceList.pages)
        ending.value = true
      else
        page.value += 1
    }

    title.value = data.showName || 'column'

    // 同步栏目标题到路由 query，供 flipnovel 公共头部读取
    if (isFlipnovelFamily && data.showName && route.query.title !== data.showName) {
      router.replace({
        query: { ...route.query, title: data.showName },
      })
    }
    allData.value = data
  }
  catch (_error) {
    error.value = _error
  }
  finally {
    fetching.value = false
  }
}
function handleBookClick(bookId: string) {
  // 仅在完读推荐列表页触发点击埋点
  if (route.query.type === 'finish') {
    finishedClick({
      elementName: 'bookclick',
      contentId: props.columnId,
      serialStatus: route.query.status === 'finish' ? 'completed' : 'ongoing',
      showNum: collections.value.length,
      title: 'finished_reclist',
      contentIdRecbook: bookId,
    })
  }

  const book = collections.value.find((item: any) => item.id === bookId)
  const firstChapterId = book?.firstChapter?.id || ''

  router.push({
    name: READER_ROUTE_NAME,
    params: { contentId: bookId, chapterId: firstChapterId },
  })
}

onMounted(() => {
  fetchCollection()
})

watch(() => [props.columnId, route.query.type], () => {
  resetState()
  fetchCollection()
})

const infiniteRef = ref<HTMLElement>()
function setInfiniteRef(el: HTMLElement | null) {
  infiniteRef.value = el || undefined
}
useInfiniteScroll(infiniteRef,
  async () => {
    if (fetching.value || ending.value || error.value)
      return

    await fetchCollection()
  },
  { distance: 50 },
)
</script>

<template>
  <CollectionPage
    :title="title"
    :collections="collections"
    :all-data="allData"
    :ending="ending"
    :fetching="fetching"
    :set-infinite-ref="setInfiniteRef"
    :handle-book-click="handleBookClick"
    :no-more-text="commonTexts.noMore"
    :loading-text="commonTexts.loading"
  />
</template>
